import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Button,
  Grid,
  Typography,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Breadcrumbs,
  Box,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import Layout from "../../layout/Layout";
import { useNavigate, useParams } from "react-router-dom";
import { config, projectMasterAPI, weeklyReportsAPI } from "../../config";
import { MobileDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import * as XLSX from "xlsx/xlsx.mjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import moment from "moment";
import DescriptionIcon from "@mui/icons-material/Description";
import { MaterialReactTable } from "material-react-table";
import MuiLink from "@mui/material/Link";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { startOfWeek } from "date-fns";
import { useSearchParams } from "react-router-dom";

function ProjectPaymentReport() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams(); // 👈 Get query params
  // Extract query params
  const queryFromDate = searchParams.get("from");
  const queryToDate = searchParams.get("to");
  const queryProjectID = searchParams.get("projectID");

  // Convert date strings to Date objects (if present)
  const parsedFromDate = queryFromDate
    ? new Date(queryFromDate)
    : startOfWeek(new Date(), { weekStartsOn: 1 });
  const parsedToDate = queryToDate ? new Date(queryToDate) : new Date();

  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const today = new Date();

  // Get Monday of current week
  const [fromDate, setFromDate] = useState(parsedFromDate);
  const [toDate, setToDate] = useState(parsedToDate);
  const [projectID, setProjectID] = useState(queryProjectID || "");

  const [uniqueTotal, setUniqueTotal] = useState({});

  const [weeklyPaymentReport, setWeeklyPaymentReport] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [ProjectNameList, setProjectNameList] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [exportExcel, setExportExcel] = useState(10);

  let tempRefs2 = useRef(false);

  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);

  const downloadExcel = () => {
    // Map contractorTotal.data to desired format (remove labour_id and set headers)
    const formattedData = exportExcel.map((item) => ({
      Date: moment(item.created_at).format("DD-MMM-YYYY"),
      "Labour Name": item.labour_name,
      "Project Name": item.project_name,
      "Labour Type": item.labour_type_name,
      "Work Type": item.work_type_name,
      "Full Days": item.full_day_count,
      "Half Days": item.half_day_count,
      Allowance: item.amount,

      Description: item.description,
      " Total Amount": item.line_total,
    }));
    const project_name = exportExcel[0]?.project_name;
    const labour_name = exportExcel[0]?.labour_name;
    const file_name = projectID != 0 ? project_name : "" + labour_name;

    const workSheet = XLSX.utils.json_to_sheet(formattedData);
    const workBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workBook, workSheet, "Weekly Report");

    XLSX.writeFile(
      workBook,
      `${file_name} -payment-report-${moment().format("DD-MM-YYYY")} .xlsx`
    );
  };

  const getWeeklyReport = () => {
    const data = {
      labourID: id === undefined ? 0 : id,
      intProjectID: projectID,
      pageSize,
      pageIndex,
      FromDate: fromDate,
      ToDate: toDate,
      intType: 2,
      sorting,
      columnFilters,
    };

    config.method = "POST";
    config.data = JSON.stringify(data);
    config.url = weeklyReportsAPI;

    axios(config)
      .then(function (response) {
        setWeeklyPaymentReport(response.data.result);
        setTotalCount(response.data.totalCount);
        setUniqueTotal(response.data.uniTotalAmount[0].total_line_total);
        setExportExcel(response.data.expExcel);
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  useEffect(() => {
    getWeeklyReport();
  }, [
    pageIndex,
    pageSize,
    fromDate,
    toDate,
    sorting,
    columnFilters,
    projectID,
  ]);

  useEffect(() => {
    if (id !== undefined) {
      getWeeklyReport();
    }
  }, [id]);

  useEffect(() => {
    if (!tempRefs2.current) {
      const data = { intType: 6 };

      config.method = "POST";
      config.data = JSON.stringify(data);
      config.url = projectMasterAPI;

      axios(config)
        .then(function (response) {
          setProjectNameList(response.data.result);
        })
        .catch(function (error) {
          console.log(error);
        });
      tempRefs2.current = true;
    }
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: "created_at",
        header: "Date",
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "-";
          const date = new Date(value);
          return `${date.getDate().toString().padStart(2, "0")}-${(
            date.getMonth() + 1
          )
            .toString()
            .padStart(2, "0")}-${date.getFullYear()}`;
        },

        enableColumnFilter: false,
      },

      {
        accessorKey: "labour_name",
        header: "Contractor Name",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const labourName = row.original.labour_name;
          return labourName != null ? labourName : "Rate Work";
        },
      },
      {
        accessorKey: "project_name",
        header: "Project Name",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "description",
        header: "Description",
        Cell: ({ row }) => {
          return row.original.description != ""
            ? row.original.description
            : "-";
        },
        enableSorting: false,
        enableColumnFilter: false,
      },

      {
        accessorKey: "labour_type_name",
        header: "Labour Type",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "full_day_count",
        header: "Full Day",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "half_day_count",
        header: "Half Day",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "amount",
        header: "Allowance",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "work_type_name",
        header: "Work Order",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "line_total",
        header: "Total Amount",
        size: 120,
        Cell: ({ cell }) => {
          const amount = cell.getValue();
          return `₹ ${new Intl.NumberFormat("en-IN", {
            maximumFractionDigits: 0,
          }).format(amount)}`;
        },
        enableSorting: false,
        enableColumnFilter: false,
        muiTableHeadCellProps: {
          sx: {
            position: "sticky",
            right: 0,
            zIndex: 2,
            backgroundColor: "#1976d2",
            color: "#fff !important",
            border: "1px solid #ccc",
          },
        },
        muiTableBodyCellProps: ({ row }) => ({
          sx: {
            position: "sticky",
            right: 0,
            zIndex: 1,
            backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
            border: "1px solid #ccc",
          },
        }),
      },
    ],
    []
  );

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <Typography className="page_title" textAlign="left">
              Weekly Payment Report
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <MuiLink underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </MuiLink>
              <Typography color="text.primary">Reports</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            container
            item
            xs={12}
            lg={6}
            justifyContent="flex-end"
            spacing={2}
          >
            <Grid item>
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIosIcon />}
              >
                Back
              </LoadingButton>
            </Grid>
            <Grid item>
              <Button
                onClick={() => downloadExcel(currentPage, rowsPerPage)}
                variant="contained"
                color="success"
                startIcon={<DescriptionIcon />}
                sx={{ borderRadius: 1 }}
              >
                Export to Excel
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          sx={{ margin: "15px 0", padding: "10px 15px 0 0" }}
        >
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="select-project-label">Select Project</InputLabel>
              <Select
                labelId="select-project-label"
                id="select-project"
                value={projectID}
                label="Select Project"
                onChange={(e) => {
                  const selectprojectID = e.target.value;
                  setProjectID(selectprojectID);
                }}
              >
                {ProjectNameList.map((row, index) => (
                  <MenuItem value={row.id} key={index}>
                    {row.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="Select From Date"
                value={fromDate}
                inputFormat="DD/MM/YYYY"
                maxDate={toDate || undefined}
                onChange={(newValue) => {
                  setFromDate(newValue);
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <MobileDatePicker
                label="Select To Date"
                value={toDate}
                inputFormat="DD/MM/YYYY"
                minDate={fromDate || undefined}
                onChange={(newValue) => {
                  setToDate(newValue);
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <LoadingButton
              size="large"
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => {
                setProjectID("");
              }}
            >
              clear
            </LoadingButton>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="text.primary"
            >
              Total Contractor Payment :{" "}
              <Box component="span" sx={{ color: "primary.main" }}>
                ₹ {Number(uniqueTotal || 0).toLocaleString()}
              </Box>
            </Typography>
          </Grid>
        </Grid>

        <MaterialReactTable
          columns={columns}
          data={weeklyPaymentReport}
          enableColumnResizing
          manualPagination
          enableGlobalFilter={false}
          enableFullScreenToggle={false}
          enableDensityToggle={false}
          enableColumnFilters={false}
          enableStickyHeader
          rowCount={totalCount}
          state={{
            isLoading: loading,
            pagination: { pageIndex, pageSize },
            sorting,
            columnFilters,
          }}
          onPaginationChange={(updater) => {
            const paginationState = updater({
              pageIndex,
              pageSize,
            });

            const { pageIndex: newPageIndex, pageSize: newPageSize } =
              paginationState;
            setPageIndex(newPageIndex);
            setPageSize(newPageSize);
          }}
          onSortingChange={setSorting}
          onColumnFiltersChange={setColumnFilters}
          muiTableBodyCellProps={{
            sx: {
              padding: "3px 8px",
              border: "1px solid #ccc",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              "& input": {
                color: "#fff !important",
                "&::placeholder": {
                  color: "#fff !important",
                  opacity: 1,
                },
              },
              "& .MuiSelect-select": {
                color: "#fff !important", // selected text color
              },
              "& .MuiSelect-icon": {
                color: "#fff !important", // dropdown arrow color
              },
              color: "#fff",
              fontWeight: "bold",
              "& svg": {
                color: "#fff",
              },
              "& .MuiTableSortLabel-root": {
                color: "#495057",
                "&.Mui-active": {
                  color: "#fff",
                  "& .MuiTableSortLabel-icon": {
                    color: "#fff !important",
                  },
                },
              },
              "& .MuiTableSortLabel-icon": {
                color: "#fff !important",
              },
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            sx: {
              borderBottom: "1px solid #ccc",
              backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
            },
          })}
          muiTableHeadCellFilterTextFieldProps={{
            sx: {
              "& input": {
                color: "#fff !important", // Change text color
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: "#fff !important", // Change underline color
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: "#fff !important", // Hover underline color
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#fff !important", // Focused underline color
              },
            },
          }}
        />
      </Grid>
    </Layout>
  );
}

export default ProjectPaymentReport;
